import type { MuscleGroup, TrainingLevel, VolumeStandard } from '../types';

/**
 * Volume landmarks per muscle group scaled by training level.
 *
 * Sources:  Renaissance Periodization recommendations,
 *           Dr. Mike Israetel's hypertrophy volume guidelines.
 *
 * Values represent WEEKLY SETS (direct working sets).
 *  MEV = Minimum Effective Volume   – below this progress is unlikely
 *  MAV = Maximum Adaptive Volume    – ideal productive range ceiling
 *  MRV = Maximum Recoverable Volume – exceeding this → overreaching
 */

type VolumeTable = Record<MuscleGroup, { mev: number; mav: number; mrv: number }>;

const INTERMEDIATE: VolumeTable = {
  chest:       { mev: 8,  mav: 14, mrv: 20 },
  front_delts: { mev: 4,  mav: 8,  mrv: 14 },
  side_delts:  { mev: 6,  mav: 14, mrv: 22 },
  rear_delts:  { mev: 6,  mav: 12, mrv: 18 },
  upper_back:  { mev: 8,  mav: 14, mrv: 22 },
  lats:        { mev: 8,  mav: 14, mrv: 20 },
  traps:       { mev: 0,  mav: 8,  mrv: 16 },
  biceps:      { mev: 4,  mav: 12, mrv: 18 },
  triceps:     { mev: 4,  mav: 10, mrv: 16 },
  forearms:    { mev: 0,  mav: 6,  mrv: 12 },
  quads:       { mev: 8,  mav: 14, mrv: 22 },
  hamstrings:  { mev: 4,  mav: 10, mrv: 16 },
  glutes:      { mev: 4,  mav: 12, mrv: 18 },
  calves:      { mev: 6,  mav: 10, mrv: 16 },
  abs:         { mev: 0,  mav: 8,  mrv: 16 },
  hip_flexors: { mev: 0,  mav: 4,  mrv: 8  },
};

/**
 * Scale factors relative to intermediate.
 * Beginners need less volume, elites need more.
 */
const LEVEL_MULTIPLIERS: Record<TrainingLevel, number> = {
  beginner:     0.65,
  intermediate: 1.00,
  advanced:     1.20,
  elite:        1.35,
};

/**
 * Get volume standards for a specific training level.
 */
export function getVolumeStandards(level: TrainingLevel): VolumeStandard[] {
  const mult = LEVEL_MULTIPLIERS[level];
  return (Object.entries(INTERMEDIATE) as [MuscleGroup, { mev: number; mav: number; mrv: number }][]).map(
    ([muscle, base]) => ({
      muscle,
      mev: Math.round(base.mev * mult),
      mav: Math.round(base.mav * mult),
      mrv: Math.round(base.mrv * mult),
    })
  );
}

/**
 * Get volume standard for a single muscle group.
 */
export function getMuscleVolumeStandard(
  muscle: MuscleGroup,
  level: TrainingLevel
): { mev: number; mav: number; mrv: number } {
  const mult = LEVEL_MULTIPLIERS[level];
  const base = INTERMEDIATE[muscle];
  return {
    mev: Math.round(base.mev * mult),
    mav: Math.round(base.mav * mult),
    mrv: Math.round(base.mrv * mult),
  };
}
